import React, {useCallback, useEffect, useState} from 'react';
import {PanelHeader} from '../../ui/blocks/PanelHeader';
import styles from './Users.module.scss';

interface ISubscription {
  type: string;
  start: number | string | Date;
  end: number | string | Date;
}

interface IUserWithSub {
  _id?: string;
  login: string;
  email?: string;
  role?: string;
  created?: number;
  subscription?: ISubscription;
}

interface IUsersMeta {
  hasNextPage: boolean;
  totalCount: number;
  page: number;
  countOnPage: number;
}

const ROLE_OPTIONS = ['admin', 'creator', 'moderator', 'user', 'guest'];

const ADMIN_NAV_ITEMS = [
  { title: 'Playlists', url: '/p/cp/playlists' },
  { title: 'Library', url: '/p/cp/tracks' },
  { title: 'Users', url: '/panel/users' },
];

export const Users = () => {
  const [users, setUsers] = useState<IUserWithSub[]>([]);
  const [meta, setMeta] = useState<IUsersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(0);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [grantingUserId, setGrantingUserId] = useState<string | null>(null);
  const [grantDays, setGrantDays] = useState<{[key: string]: string}>({});

  const countOnPage = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any[] = [];
      if (searchFilter) {
        filters.push({
          type: 'aggregation',
          aggregation: 'OR',
          filter: [
            { field: 'login', operator: 'like', value: searchFilter },
            { field: 'email', operator: 'like', value: searchFilter },
          ],
        });
      }

      const params = {
        filter: filters,
        pagination: { page, countOnPage },
      };

      const response = await fetch(
        `/api/custom/users/list?params=${encodeURIComponent(JSON.stringify(params))}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const result = await response.json();
        setUsers(result.data || []);
        setMeta(result.meta || null);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    } finally {
      setLoading(false);
    }
  }, [searchFilter, page, countOnPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(0);
    setSearchFilter(searchQuery.trim());
  };

  const handleRoleChange = async (user: IUserWithSub, newRole: string) => {
    if (!user._id) return;
    setSavingUserId(user._id);
    try {
      const response = await fetch('/api/users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _id: user._id, role: newRole }),
        credentials: 'include',
      });
      if (response.ok) {
        fetchUsers();
      }
    } catch (e) {
      console.error('Failed to update role:', e);
    } finally {
      setSavingUserId(null);
    }
  };

  const handleDelete = async (user: IUserWithSub) => {
    if (!window.confirm(`Удалить пользователя "${user.login}"? Это действие нельзя отменить.`)) return;
    try {
      const response = await fetch('/api/users/delete_by_user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: user.login }),
        credentials: 'include',
      });
      if (response.ok) {
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`Ошибка: ${error.error || error.message}`);
      }
    } catch (e: any) {
      alert(`Ошибка: ${e?.message || e}`);
    }
  };

  const handleGrantSubscription = async (user: IUserWithSub) => {
    if (!user._id) return;
    const days = parseInt(grantDays[user._id] || '30') || 30;
    setGrantingUserId(user._id);
    try {
      const response = await fetch('/api/custom/users/grant_subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user._id, days }),
        credentials: 'include',
      });
      if (response.ok) {
        fetchUsers();
        setGrantDays(prev => ({ ...prev, [user._id!]: '' }));
      } else {
        const error = await response.json();
        alert(`Ошибка: ${error.error || error.message}`);
      }
    } catch (e: any) {
      alert(`Ошибка: ${e?.message || e}`);
    } finally {
      setGrantingUserId(null);
    }
  };

  const handleRevokeSubscription = async (user: IUserWithSub) => {
    if (!user._id) return;
    if (!window.confirm(`Отозвать подписку у "${user.login}"?`)) return;
    setGrantingUserId(user._id);
    try {
      const response = await fetch('/api/custom/users/revoke_subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user._id }),
        credentials: 'include',
      });
      if (response.ok) {
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`Ошибка: ${error.error || error.message}`);
      }
    } catch (e: any) {
      alert(`Ошибка: ${e?.message || e}`);
    } finally {
      setGrantingUserId(null);
    }
  };

  const getEndTimestamp = (end: any): number => {
    if (!end) return 0;
    if (typeof end === 'number') return end;
    if (typeof end === 'string' || end instanceof Date) {
      return new Date(end).getTime();
    }
    return 0;
  };

  const formatDate = (end: any) => {
    const ts = getEndTimestamp(end);
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isSubscriptionActive = (sub?: ISubscription) => {
    if (!sub) return false;
    const endTs = getEndTimestamp(sub.end);
    return endTs > Date.now();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <PanelHeader title="Beatless" navItems={ADMIN_NAV_ITEMS} />

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <h1 className={styles.pageTitle}>Управление пользователями</h1>

        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="#99a1af" strokeWidth="1.67"/>
              <path d="M13.5 13.5L17 17" stroke="#99a1af" strokeWidth="1.67" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Поиск по логину или email..."
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button className={styles.searchBtn} onClick={handleSearch}>Найти</button>
        </div>

        {meta && (
          <div className={styles.resultsInfo}>
            <span className={styles.resultsCount}>
              Всего: {meta.totalCount} пользователей
            </span>
          </div>
        )}

        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loadingState}>Загрузка...</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Логин</th>
                  <th>Email</th>
                  <th>Роль</th>
                  <th>Подписка</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td className={styles.loginCell}>{user.login}</td>
                    <td className={styles.cell}>{user.email || '—'}</td>
                    <td className={styles.cell}>
                      <select
                        className={styles.roleSelect}
                        value={user.role || 'user'}
                        disabled={savingUserId === user._id}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                      >
                        {ROLE_OPTIONS.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      {savingUserId === user._id && (
                        <span className={styles.savingIndicator}>...</span>
                      )}
                    </td>
                    <td className={styles.subscriptionCell}>
                      {user.subscription ? (
                        <div className={styles.subscriptionInfo}>
                          <span className={`${styles.subscriptionBadge} ${isSubscriptionActive(user.subscription) ? styles.active : styles.expired}`}>
                            {isSubscriptionActive(user.subscription) ? 'Активна' : 'Истекла'}
                          </span>
                          <span className={styles.subscriptionDate}>
                            до {formatDate(user.subscription.end)}
                          </span>
                          <button
                            className={`${styles.subBtn} ${styles.revokeBtn}`}
                            onClick={() => handleRevokeSubscription(user)}
                            disabled={grantingUserId === user._id}
                            title="Отозвать подписку"
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <span className={styles.noSubscription}>Нет</span>
                      )}
                      <div className={styles.grantSubscription}>
                        <input
                          type="number"
                          className={styles.daysInput}
                          placeholder="дней"
                          min="1"
                          max="365"
                          value={grantDays[user._id || ''] || ''}
                          onChange={(e) => setGrantDays(prev => ({ ...prev, [user._id!]: e.target.value }))}
                        />
                        <button
                          className={`${styles.subBtn} ${styles.grantBtn}`}
                          onClick={() => handleGrantSubscription(user)}
                          disabled={grantingUserId === user._id}
                          title="Выдать подписку"
                        >
                          {grantingUserId === user._id ? '...' : '+'}
                        </button>
                      </div>
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        className={`${styles.actionBtn} ${styles.actionDanger}`}
                        onClick={() => handleDelete(user)}
                        title="Удалить"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="#e7000b" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6.66667 7.33337V11.3334M9.33334 7.33337V11.3334" stroke="#e7000b" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {meta && (meta.hasNextPage || meta.page > 0) && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              disabled={meta.page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              Назад
            </button>
            <span className={styles.pageInfo}>
              Страница {meta.page + 1}
            </span>
            <button
              className={styles.paginationBtn}
              disabled={!meta.hasNextPage}
              onClick={() => setPage(p => p + 1)}
            >
              Вперёд
            </button>
          </div>
        )}
      </main>
    </div>
  );
};
